import * as XLSX from 'xlsx';
import { costCodeKey } from '@/lib/cost-codes';

/**
 * The Items CSV import as pure functions: read the file, map its columns to the item fields, validate every row against the catalog as it
 * is NOW, and say what will happen to each row. Nothing is written here. An item that already exists is SKIPPED -- never updated -- so an
 * import can neither duplicate an item nor overwrite a price. "Exists" is decided by identity, not by a name alone (names are not unique:
 * two different products may share one):
 *   - a row WITH a SKU matches only the item that has that SKU;
 *   - a row WITHOUT a SKU matches an item with the same name AND cost code AND unit.
 */
export type ImportField = 'name' | 'sku' | 'description' | 'cost_code' | 'unit' | 'vendor' | 'unit_price';

export const IMPORT_FIELDS: Array<{ field: ImportField; label: string; required?: boolean; hints: string[] }> = [
  { field: 'name', label: 'Item name', required: true, hints: ['name', 'item', 'item name', 'product', 'description'] },
  { field: 'sku', label: 'SKU', hints: ['sku', 'code', 'internal code', 'item code', 'part number', 'part #'] },
  { field: 'description', label: 'Description', hints: ['description', 'details', 'notes'] },
  { field: 'cost_code', label: 'Cost code', hints: ['cost code', 'costcode', 'cost_code', 'category code'] },
  { field: 'unit', label: 'Unit', hints: ['unit', 'uom', 'unit of measure'] },
  { field: 'vendor', label: 'Vendor (for the price)', hints: ['vendor', 'supplier'] },
  { field: 'unit_price', label: 'Unit price', hints: ['unit price', 'price', 'cost', 'unit cost'] },
];

export const MAX_IMPORT_ROWS = 500;

export interface ParsedCsv {
  headers: string[];
  rows: string[][];
  truncated: boolean;
}

/** Reads CSV text (quotes, commas inside quotes, CRLF) into headers + rows of text. Row 0 of `rows` is line 2 of the file. */
export function parseCsvText(text: string): ParsedCsv {
  const workbook = XLSX.read(text, { type: 'string', raw: true });
  const sheet = workbook.Sheets[workbook.SheetNames[0]];
  const matrix = (XLSX.utils.sheet_to_json(sheet, { header: 1, raw: false, defval: '', blankrows: true }) as unknown[][]).map((row) => row.map((cell) => String(cell ?? '').trim()));
  if (matrix.length === 0) return { headers: [], rows: [], truncated: false };
  const [headers, ...rows] = matrix;
  // blank lines stay in place (so a message can name the real line of the file); the preview skips them
  while (rows.length > 0 && rows[rows.length - 1].every((cell) => cell === '')) rows.pop();
  return { headers, rows: rows.slice(0, MAX_IMPORT_ROWS), truncated: rows.length > MAX_IMPORT_ROWS };
}

const norm = (value: string) => value.toLowerCase().replace(/[^a-z0-9]+/g, ' ').trim();

/** column index per field (or -1), guessed from the header names -- the user can change every one. */
export function autoMapColumns(headers: string[]): Record<ImportField, number> {
  const mapping = {} as Record<ImportField, number>;
  const used = new Set<number>();
  for (const { field, hints } of IMPORT_FIELDS) {
    mapping[field] = -1;
    for (const hint of hints) {
      const index = headers.findIndex((header, i) => !used.has(i) && norm(header) === hint);
      if (index !== -1) { mapping[field] = index; used.add(index); break; }
    }
  }
  return mapping;
}

export type RowStatus = 'new' | 'duplicate' | 'error';

export interface PreviewRow {
  line: number; // 1-based line in the file (header = 1)
  status: RowStatus;
  message: string;
  name: string;
  sku: string;
  description: string;
  unit: string;
  costCodeId: string | null;
  costCodeText: string;
  vendorId: string | null;
  vendorText: string;
  unitPrice: number | null;
}

export interface ImportContext {
  existingItems: Array<{ name: string; sku: string | null; costCodeId: string | null; unit: string | null }>;
  /** the CHILD cost codes only */
  costCodes: Array<{ id: string; code: string }>;
  vendors: Array<{ id: string; name: string }>;
}

const key = (value: string) => value.trim().toLowerCase();

export function buildPreview(rows: string[][], mapping: Record<ImportField, number>, context: ImportContext): PreviewRow[] {
  // the identity of an item without a SKU: name + cost code + unit
  const nameKey = (name: string, costCodeId: string | null, unit: string | null) => `${key(name)}|${costCodeId ?? ''}|${key(unit ?? '')}`;
  const existingNames = new Set(context.existingItems.map((item) => nameKey(item.name, item.costCodeId, item.unit)));
  const existingSkus = new Set(context.existingItems.filter((item) => item.sku).map((item) => key(item.sku as string)));
  const codeByText = new Map(context.costCodes.map((c) => [costCodeKey(c.code), c.id]));
  const vendorByName = new Map(context.vendors.map((v) => [key(v.name), v.id]));
  const seen = new Set<string>();
  const cell = (row: string[], field: ImportField) => (mapping[field] >= 0 ? (row[mapping[field]] ?? '').trim() : '');

  const previewRows: PreviewRow[] = [];
  rows.forEach((row, index) => {
    if (row.every((cell) => cell === '')) return; // a blank line of the file
    const out: PreviewRow = {
      line: index + 2, status: 'new', message: '', name: cell(row, 'name'), sku: cell(row, 'sku'), description: cell(row, 'description'), unit: cell(row, 'unit'),
      costCodeId: null, costCodeText: cell(row, 'cost_code'), vendorId: null, vendorText: cell(row, 'vendor'), unitPrice: null,
    };
    const fail = (message: string) => { out.status = 'error'; out.message = message; return out; };
    previewRows.push(out);

    if (!out.name) return fail('Item name is empty');
    if (out.name.length > 200) return fail('Item name is longer than 200 characters');
    if (out.sku.length > 60) return fail('SKU is longer than 60 characters');
    if (out.unit.length > 20) return fail('Unit is longer than 20 characters');
    // every item needs a CHILD cost code (`context.costCodes` lists only those)
    if (!out.costCodeText) return fail('Cost code is required');
    const codeId = codeByText.get(costCodeKey(out.costCodeText));
    if (!codeId) return fail(`Cost code "${out.costCodeText}" does not exist or is a parent cost code`);
    out.costCodeId = codeId;
    const priceText = cell(row, 'unit_price');
    if (priceText) {
      const price = Number(priceText.replace(/[$,\s]/g, ''));
      if (!Number.isFinite(price) || price < 0) return fail(`Unit price "${priceText}" is not a valid price`);
      out.unitPrice = price;
    }
    if (out.vendorText) {
      const id = vendorByName.get(key(out.vendorText));
      if (!id) return fail(`Vendor "${out.vendorText}" is not one of your vendors`);
      out.vendorId = id;
    }
    if (out.unitPrice !== null && !out.vendorId) return fail('A price needs a vendor');
    if (out.vendorId && out.unitPrice === null) return fail('A vendor needs a price');

    // duplicates: against the catalog and against earlier rows of the same file -- skipped, never updated
    const identity = out.sku ? `sku:${key(out.sku)}` : `name:${nameKey(out.name, out.costCodeId, out.unit)}`;
    const inCatalog = out.sku ? existingSkus.has(key(out.sku)) : existingNames.has(nameKey(out.name, out.costCodeId, out.unit));
    if (inCatalog) {
      out.status = 'duplicate';
      out.message = out.sku ? 'An item with this SKU is already in the catalog -- skipped (nothing is overwritten)' : 'An item with this name, cost code and unit is already in the catalog -- skipped (nothing is overwritten)';
    } else if (seen.has(identity)) {
      out.status = 'duplicate';
      out.message = 'Repeated in this file -- skipped';
    }
    if (out.status === 'new') seen.add(identity);
    return out;
  });
  return previewRows;
}

export const countByStatus = (rows: PreviewRow[]) => ({
  new: rows.filter((r) => r.status === 'new').length,
  duplicate: rows.filter((r) => r.status === 'duplicate').length,
  error: rows.filter((r) => r.status === 'error').length,
});
